
import { Range } from "@/app/ui/components/date-range-picker/type";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import {
  RequestListSummary,
  ListSummaryItem,
} from "@/domain/entities/PickupDashboardSummary";
import HeaderFilterBar from "@/app/ui/views/pickup-dashboard/monitoring/components/header-filter-bar/index.vue";
import { computed, defineComponent, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  formatDateNumber,
  parsingErrorResponse,
} from "@/app/infrastructures/misc/Utils";
import { formatCompactNumber as fmtCompact } from "@/app/infrastructures/misc/Utils";
import { CourierSummaryRequest } from "@/data/payload/api/PickupDashboardRequests";
import LpButton from "@/app/ui/components/lp-button/index.vue";
import ManualAssignModal from "../components/modal-manual-assign/index.vue";
import TransferPickupModal from "../courier/transfer-pickup/component/modal.vue";
import { OptionsClass } from "@/domain/entities/MainApp";
import { debounce } from "lodash";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ExportApiRequest } from "@/data/payload/api/PickupDashboardApiRequest";
import { ReportController } from "@/app/ui/controllers/ReportController";
import { DownloadReportRequest } from "@/domain/entities/Kulioner";
import i18n from "@/i18n";

interface SelectedSellerData {
  id: string;
  sellerName: string;
  sellerPhone: string;
  packageCount: number;
  koli: number;
  weight: number;
  taskGroupId?: number;
  index: number;
}

export default defineComponent({
  name: "TabsListSummary",

  components: {
    HeaderFilterBar,
    LpButton,
    ManualAssignModal,
    TransferPickupModal,
  },

  setup() {
    const t = (key: string, params?: Record<string, unknown>) =>
      String(i18n.global.t(key, params));
    const dateRange = ref<Range>(new Range());
    const search = ref<string>("");
    const shipmentTyp = ref<OptionsClass[]>([]);

    const router = useRouter();
    const route = useRoute();

    const showAssignModal = ref(false);
    const selectedSellersForAssign = ref<SelectedSellerData[]>([]);

    const showTransferModal = ref(false);
    const selectedSellers = ref<SelectedSellerData[]>([]);
    const sourceCourierId = ref(0);
    const buttonProps = ref<{
      titleKey: string;
      icon: string;
      selectedCountKey: string;
    }>({
      titleKey: "",
      icon: "",
      selectedCountKey: ""
    });

    const getCrrotw = computed(
      () => PickupDashboardController.crrotw.seller || 0
    );
    const getCrrpic = computed(
      () => PickupDashboardController.crrpic.shipment || 0
    );
    const getCrrdon = computed(
      () => PickupDashboardController.crrdon.shipment || 0
    );
    const getCrrnfd = computed(
      () => PickupDashboardController.crrnfd.seller || 0
    );

    const selectedRows = computed(
      () => PickupDashboardController.selectedRowsPickup
    );

    const lastSegment = computed(() => route.path.split("/").pop());

    const detailSummary = computed(
      () => PickupDashboardController.detailSummary
    );

    const isLoading = computed(
      () => PickupDashboardController.loadingDetailSummary
    );

    const formatCompactNumber = (val: number) => {
      return fmtCompact(val);
    };

    const openAssignModal = (item: ListSummaryItem[]) => {
      selectedSellersForAssign.value = item.map((summary, index) => {
        return {
          id: summary.shipmentId.toString(),
          sellerName: summary.senderName,
          sellerPhone: summary.senderPhone,
          packageCount: summary.totalShipment,
          koli: summary.totalQuantity,
          weight: summary.totalWeight,
          index: index,
        };
      });
      showAssignModal.value = true;
    };

    const openTransferModal = (item: ListSummaryItem[]) => {
      selectedSellers.value = item.map((summary, index) => {
        const uniqueId = summary.incrementId?.toString();
        return {
          id: uniqueId,
          sellerName: summary.senderName,
          sellerPhone: summary.senderPhone,
          packageCount: summary.totalShipment,
          koli: summary.totalQuantity,
          weight: summary.totalWeight,
          taskGroupId: summary.taskId,
          index: index,
        };
      });
      sourceCourierId.value = 0;
      showTransferModal.value = true;
    };

    const handleRemoveSeller = (sellerIndex: number) => {
      selectedSellers.value = selectedSellers.value.filter(
        (seller) => seller.index !== sellerIndex
      );
    };

    const tabsSummary = computed(() => {
      return [
        {
          name: "crrotw",
          title: `${t("pickupDashboard.belumDiPickUp")} (${formatCompactNumber(
            getCrrotw.value
          )})`
        },
        {
          name: "crrpic",
          title: `${t("pickupDashboard.belumDisetor")} (${formatCompactNumber(
            getCrrpic.value
          )})`
        },
        {
          name: "crrdon",
          title: `${t("pickupDashboard.belumBkd")} (${formatCompactNumber(
            getCrrdon.value
          )})`
        },
        {
          name: "crrnfd",
          title: `${t("pickupDashboard.belumDiassign")} (${formatCompactNumber(
            getCrrnfd.value
          )})`
        }
      ];
    });

    const fetchSummaryTabsList = async () => {
      const filterResult = PickupDashboardController.currentFilter;
      const start = filterResult.startDate || dateRange.value.start;
      const end = filterResult.endDate || dateRange.value.end;

      const params = new CourierSummaryRequest({
        q: filterResult.q,
        shipmentType: filterResult.shipmentType,
        startDate: typeof start === "string" ? start : formatDateNumber(start),
        endDate: typeof end === "string" ? end : formatDateNumber(end),
        status: "CRROTW,CRRPIC,CRRDON,CRRNFD",
      });
      await PickupDashboardController.getCourierSummary(params);
    };

    const camelToSnake = (value: string): string => {
      return value.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    };

    const fetchListSummary = async () => {
      const filterResult = PickupDashboardController.currentFilter;

      await PickupDashboardController.getListSummary(
        new RequestListSummary({
          q: filterResult.q,
          startDate: filterResult.startDate,
          endDate: filterResult.endDate,
          status: filterResult.status,
          shipmentType: filterResult.shipmentType,
          sortBy: filterResult.sortBy,
          orderBy: filterResult.orderBy,
        })
      );
    };

    const handleModalSuccess = async () => {
      // Reset selected sellers and close modal
      selectedSellersForAssign.value = [];
      selectedSellers.value = [];
      showAssignModal.value = false;
      showTransferModal.value = false;

      // Refresh list

      await fetchListSummary();
      await fetchSummaryTabsList();
    };

    const goBack = () => router.push("/pickup-dashboard");

    const onSearch = debounce(async (value: string) => {
      const filterResult = PickupDashboardController.currentFilter;

      PickupDashboardController.setFilter({
        q: value.trim(),
        startDate: formatDateNumber(dateRange.value.start),
        endDate: formatDateNumber(dateRange.value.end),
        shipmentType: filterResult.shipmentType,
        sortBy: filterResult.sortBy,
        orderBy: filterResult.orderBy
      });
      await fetchListSummary();
      await fetchSummaryTabsList();
    }, 250);

    const onChangeDate = async (date: any) => {
      const filterResult = PickupDashboardController.currentFilter;

      dateRange.value = date;
      PickupDashboardController.setFilter({
        q: search.value,
        startDate: formatDateNumber(dateRange.value.start),
        endDate: formatDateNumber(dateRange.value.end),
        shipmentType: filterResult.shipmentType,
      });
      await fetchListSummary();
      await fetchSummaryTabsList();
    };

    const onDownloadPickUp = debounce(async (date: DownloadReportRequest) => {
      const cf = PickupDashboardController.currentFilter;
      try {
        MainAppController.showLoading();

        await PickupDashboardController.export(
          new ExportApiRequest({
            ...date,
            q: cf.q,
            shipmentType: Array.isArray(cf.shipmentType)
              ? cf.shipmentType.map((ts: any) => ts.value).join(",")
              : "",
          })
        );

        await ReportController.onGetListDownloadReport();
        ReportController.setOpenDownloadRequest(true);
      } catch (err) {
        MainAppController.showErrorMessage(
          parsingErrorResponse(
            err,
            t("pickupDashboard.downloadFailedTitle"),
            onDownloadPickUp
          )
        );
      } finally {
        MainAppController.closeLoading();
      }
    }, 400);

    const onChoseShipmentType = async () => {
      const shipemntTypeMap = Array.isArray(shipmentTyp.value)
        ? shipmentTyp.value.map((ts: OptionsClass) => ts.value).join(",")
        : "";

      PickupDashboardController.setFilter({
        shipmentType: shipemntTypeMap,
      });

      fetchListSummary();
      fetchSummaryTabsList();
    };

    const onBooking = (item: string[]) => {
      const shipmentIds = item.join(",");
      router.push(`/booking-v2/create/by-shipment-id?bulk=${shipmentIds}`);
    };

    const onHandleButtonAction = () => {
      const shipmentIds = selectedRows.value.map((item) => item.shipmentId);

      switch (lastSegment.value) {
        case "crrotw":
          openTransferModal(selectedRows.value);
          break;

        case "crrpic":
          onBooking(shipmentIds);
          break;

        case "crrdon":
          onBooking(shipmentIds);
          break;

        case "crrnfd":
          openAssignModal(selectedRows.value);
          break;
      }
    };

    const onHandleTabAction = (tabName?: string) => {
      const tab = tabName || lastSegment.value;
      switch (tab) {
        case "crrotw":
          buttonProps.value.titleKey = "pickupDashboard.transferPickUp";
          buttonProps.value.icon = "transfer-ic";
          buttonProps.value.selectedCountKey = "pickupDashboard.selectedSender";
          PickupDashboardController.setFilter({
            status: "CRROTW",
            sortBy: "DESC",
            orderBy: "total_shipment"
          });
          break;

        case "crrpic":
          buttonProps.value.titleKey = "pickupDashboard.booking";
          buttonProps.value.icon = "calendar-plus";
          buttonProps.value.selectedCountKey =
            "pickupDashboard.selectedShipment";
          PickupDashboardController.setFilter({
            status: "CRRPIC",
            sortBy: "DESC",
            orderBy: "status_at"
          });
          break;

        case "crrdon":
          buttonProps.value.titleKey = "pickupDashboard.booking";
          buttonProps.value.icon = "calendar-plus";
          buttonProps.value.selectedCountKey =
            "pickupDashboard.selectedShipment";
          PickupDashboardController.setFilter({
            status: "CRRDON",
            sortBy: "DESC",
            orderBy: "status_at"
          });
          break;

        case "crrnfd":
          buttonProps.value.titleKey = "pickupDashboard.assignPickUp";
          buttonProps.value.icon = "transfer-ic";
          buttonProps.value.selectedCountKey = "pickupDashboard.selectedSender";
          PickupDashboardController.setFilter({
            status: "CRRNFD",
            sortBy: "DESC",
            orderBy: "",
          });
          break;

        default:
          break;
      }
    };

    onMounted(async () => {
      // Reset filters when component mounts
      PickupDashboardController.setFilter({
        q: "",
        startDate: "",
        endDate: "",
        shipmentType: "",
        orderBy: "",
      });

      // set filter when any date on query params
      if (route.query.start_date && route.query.end_date) {
        const start = String(route.query.start_date);
        const end = String(route.query.end_date);
        dateRange.value = new Range({
          start: new Date(start),
          end: new Date(end),
        });
        PickupDashboardController.setFilter({
          startDate: formatDateNumber(new Date(start)),
          endDate: formatDateNumber(new Date(end)),
        });
      }

      onHandleTabAction();

      await fetchSummaryTabsList();
      await fetchListSummary();
    });

    return {
      dateRange,
      search,
      tabsSummary,
      selectedRows,
      shipmentTyp,
      goBack,
      onSearch,
      onChangeDate,
      onDownloadPickUp,
      onChoseShipmentType,
      onHandleButtonAction,
      showAssignModal,
      selectedSellersForAssign,
      showTransferModal,
      selectedSellers,
      sourceCourierId,
      buttonProps,
      openAssignModal,
      openTransferModal,
      handleModalSuccess,
      handleRemoveSeller,
      onHandleTabAction,
      detailSummary,
      isLoading,
    };
  },
});
